import { GameLoader } from "./loaders/GameLoader";
import { LobbyService } from "./services/LobbyService";
import { CommunicationService } from "./services/CommunicationService";
import { addLoader, createIframe, showError } from "./utils";
import { PromoCommunicationService } from "./services/PromoCommunicationService";
import { SentryUtils } from "../utils/sentry.utils";
import { getEnvConfig } from "../utils/env.utils";
import {TypeInitParams} from "./types/TypeInitParams";
import {handleGoReelsProtocol, handleGoReelsReplayProtocol} from "./mappers/GoReels.integration";
import {TypePlatform} from "./types/IGameLoader.types";
import { GameReloadHandler } from "./handlers/GameReloadHandler";
import "../style.css";

const getParams = (): TypeInitParams => {
    const params = new URLSearchParams(window.location.search);
    // GoReels launch protocol
    handleGoReelsProtocol(params);
    handleGoReelsReplayProtocol(params);

    const gameName = params.get("gameName") || params.get("game");
    const partner = params.get("gm_partner") || params.get("partner") || undefined;
    const platform = (params.get("platform") || "mob") as TypePlatform;
    const debug = params.get("debug") || undefined
    const promo_widget = params.get("promo_widget") || undefined;
    const key = params.get("token") || params.get("key") || undefined;

    if (!gameName) {
        throw new Error("gameName couldn't be found in the URL");
    }

    return {
        gameName,
        partner,
        debug,
        platform,
        params,
        promo_widget,
        key
    };
};

(async () => {
    try {
        const initParams = getParams();
        const { gameName, params} = initParams;

        const gameLoader = new GameLoader(initParams);
        await gameLoader.init();

        const finalURL = (() => {
            const url = gameLoader.generateUrl(gameName);

            // redirect to replay server for find custom_params - GT-5712
            if (!!gameLoader.global.configs.replay_custom_params_url) {
                const customParams = new URL(gameLoader.global.configs.replay_custom_params_url);
                customParams.searchParams.set('redirect', url);
                return customParams.toString();
            }

            return url;
        })();

        /**
         * Feature Redirect Mode
         *
         * Used when Bash acts primarily as a navigator for launching games.
         *
         * Cases:
         * - Orbit Asia: games use a unified path (Shell → Bash → Game), so Orbit
         *   cannot determine whether a BNG producer's game should be launched via PLS or BNG.
         * - Custom launches: used for teams like BadDog when game launch parameters
         *   depend on conditions that Orbit cannot customize dynamically.
         * - Replay: Bash may open itself inside an iframe/popup. To avoid displaying
         *   two Bash instances, the inner Bash redirects directly to the game URL.
         */
        if (!!params.get("frame-redirect") && params.get("frame-redirect") === "1") {
            window.location.replace(finalURL);
            return;
        }

        const rootContainer = document.getElementById("game-container");
        if (!rootContainer) {
            throw new Error("Root container for the game not found.");
        }

        let loader = addLoader();
        let loaderHidden = false;
        let loadInc = 0;

        document.body.appendChild(loader);

        const countLoadEvents = 1;

        const iframe = createIframe(finalURL);

        const onLoaded = () => {
            loadInc++;
            if (!loaderHidden && loader && loadInc >= countLoadEvents) {
                loaderHidden = true;
                loader.classList.add("hide");

                setTimeout(() => {
                    document.body.removeChild(loader);
                    loader.remove();
                    iframe.removeEventListener("load", onLoaded);
                }, 500);
            }
        };

        iframe.addEventListener("load", onLoaded);
        rootContainer.appendChild(iframe);

        const envConfig = await getEnvConfig();
        SentryUtils.init(envConfig);

        SentryUtils.setGlobalConfigs(gameLoader.global.configs);
        void SentryUtils.setSentryTags();

        SentryUtils.startGameLoadTimer();

        const isDebug = gameLoader.global?.configs?.debug === "1";
        let promoCommunicationService: PromoCommunicationService | null = null;

        if (gameLoader.global?.configs?.promo_widget === "1") {
            promoCommunicationService = new PromoCommunicationService(
                gameLoader,
                iframe,
                isDebug
            );
        }

        const reloadHandler = new GameReloadHandler(
            promoCommunicationService,
            isDebug,
        );

        new CommunicationService(gameLoader, iframe, reloadHandler, initParams, envConfig);
        new LobbyService(gameLoader, iframe);
    } catch (eg: any) {
        console.error(eg.message, eg.stack);
        showError(`Error loading game: ${eg.message}`);
    }
})();
